<script lang="ts">
	/**
	 * One counted figure on the admin overview.
	 *
	 * Extracted because the section had four copies of the same twelve lines and was about to have
	 * ten: a tinted icon chip, a number, a caption. The repetition is what let the four drift —
	 * each carried its own spinner markup — and a tile that renders its loading state differently
	 * from the one beside it reads as one figure having failed.
	 *
	 * It is a **count**, never money: a money figure is per currency and goes through
	 * `CurrencyFigure`, which renders one line per currency and deliberately offers no total.
	 */

	/** Font Awesome solid icon name, without the `fa-` prefix. */
	export let faIcon: string = 'circle';
	/**
	 * The chip's colour. Whole literal class strings, never built at runtime — a class assembled
	 * from a prop is invisible to the Tailwind JIT scanner and the style silently never ships.
	 */
	export let tone: 'blue' | 'green' | 'yellow' | 'purple' | 'red' | 'sky' = 'blue';
	export let label: string = '';
	/** The figure. Rendered as given, so a caller can pass `12%` or `—`. */
	export let value: string | number = 0;
	export let loading: boolean = false;
	/** A second line under the caption, for what the figure does not say on its own. */
	export let detail: string = '';

	const CHIPS: Record<string, string> = {
		blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
		green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
		yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
		purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
		red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
		sky: 'bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400'
	};
</script>

<div class="widget bg-depth p-4">
	<div class="flex items-center gap-3">
		<div class="p-3 rounded-lg shrink-0 {CHIPS[tone]}">
			<i class="fa-solid fa-{faIcon}"></i>
		</div>
		<div class="min-w-0">
			<p class="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
				{#if loading}
					<i class="fa-solid fa-spinner fa-spin text-lg"></i>
				{:else}
					{value}
				{/if}
			</p>
			<p class="text-sm text-default">{label}</p>
			{#if detail.length > 0}
				<p class="text-xs text-default">{detail}</p>
			{/if}
		</div>
	</div>
</div>
